import {
  forwardRef,
  ComponentPropsWithoutRef,
  ReactNode,
  useState,
  useEffect,
} from "react";
import cx from "classnames";

import styles from "./Label.module.scss";
import { Icon } from "../Icon";
import Info from "@servicetitan/hammer-icon/mdi/round/info.svg";
import { Tooltip, TooltipProps } from "../Tooltip";
import { SrOnly } from "../SrOnly";

/**
 * Props for the Label component.
 */
export type LabelProps = Omit<ComponentPropsWithoutRef<"label">, "id"> & {
  /** Unique identifier for the label element */
  id?: string;
  /**
   * Whether the field is required. Shows a red asterisk (*) when true.
   * @default false
   */
  required?: boolean;
  /** Additional information to display in a tooltip */
  moreInfo?: ReactNode;
  /**
   * Controls the open state of the more info tooltip.
   * @default false
   */
  openMoreInfo?: TooltipProps["open"];
};

/**
 * Internal component for rendering the info icon with tooltip.
 *
 * @param props - Component props
 * @param props.moreInfo - The content to display in the tooltip
 * @param props.openMoreInfo - Controls the tooltip open state
 */
export const LabelMoreInfoIcon = ({
  moreInfo,
  openMoreInfo,
}: {
  moreInfo: LabelProps["moreInfo"];
  openMoreInfo: LabelProps["openMoreInfo"];
}) => {
  const [open, setOpen] = useState(openMoreInfo);

  useEffect(() => {
    if (openMoreInfo) {
      setOpen(true);
    } else {
      // close the tooltip
      setOpen(false);

      // reset to uncontrolled state
      setTimeout(() => {
        setOpen(undefined);
      });
    }
  }, [openMoreInfo]);

  return (
    <Tooltip
      open={open}
      placement="top"
      offset={5}
      fallbackPlacements={["right", "bottom", "left"]}
    >
      <Tooltip.Trigger className={styles["info-trigger"]}>
        <Icon svg={Info} />
      </Tooltip.Trigger>
      <Tooltip.Content className={styles["info-content"]}>
        {moreInfo}
      </Tooltip.Content>
    </Tooltip>
  );
};

/**
 * Label component for form field labels with optional required indicator and help tooltip.
 *
 * Features:
 * - Accessible form label with proper semantic HTML
 * - Optional required field indicator with red asterisk
 * - Help tooltip with info icon for additional context
 * - Screen reader support for required field announcement
 * - Controlled and uncontrolled tooltip state management
 * - Flexible content support for label text and help information
 * - Proper ARIA attributes and semantic structure
 * - Responsive design with consistent typography
 *
 * @example
 * <Label htmlFor="email" required>
 *   Email Address
 * </Label>
 * <input id="email" type="email" />
 *
 * @example
 * <Label
 *   htmlFor="password"
 *   required
 *   moreInfo="Password must be at least 8 characters long"
 * >
 *   Password
 * </Label>
 * <input id="password" type="password" />
 */
export const Label = forwardRef<HTMLLabelElement, LabelProps>((props, ref) => {
  const { children, className, moreInfo, required, openMoreInfo, ...rest } =
    props;
  const LabelClassNames = cx([styles["label"]], className);

  return (
    <label className={LabelClassNames} data-anv="label" ref={ref} {...rest}>
      <div className={styles["top-container"]} aria-atomic="true">
        <span className={styles["label-text"]}>
          {children}
          {required ? (
            <>
              <span aria-hidden className={styles["required"]}>
                *
              </span>
              <SrOnly>Required</SrOnly>
            </>
          ) : null}
        </span>
        {moreInfo ? (
          <LabelMoreInfoIcon moreInfo={moreInfo} openMoreInfo={openMoreInfo} />
        ) : null}
      </div>

      {moreInfo ? <SrOnly>{moreInfo}</SrOnly> : null}
    </label>
  );
});

Label.displayName = "Label";
